import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Form, Modal, Select, message, Popconfirm, Tag, Row, Col, InputNumber, Card } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { customerAPI } from '../../services/api';
import ImportExportButtons from '../../components/ImportExportButtons';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({ level: '', type: '', status: '' });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [form] = Form.useForm();

  // 客户类型映射
  const typeMap = {
    'manufacturer': '生产企业',
    'distributor': '经销商',
    'retailer': '零售商',
    'end_user': '终端用户'
  };

  // 客户等级颜色
  const levelColors = {
    'A': 'gold',
    'B': 'blue',
    'C': 'green',
    'D': 'default'
  };

  // 状态映射
  const statusMap = {
    'active': { text: '正常', color: 'green' },
    'inactive': { text: '停用', color: 'default' },
    'blacklist': { text: '黑名单', color: 'red' }
  };

  // 付款方式映射
  const paymentMethodMap = {
    'cash': '现金',
    'transfer': '银行转账',
    'check': '支票',
    'acceptance': '承兑汇票',
    'credit': '赊销'
  };

  const columns = [
    {
      title: '客户编码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      fixed: 'left'
    },
    {
      title: '客户名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true
    },
    {
      title: '简称',
      dataIndex: 'shortName',
      key: 'shortName',
      width: 100
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (text) => typeMap[text] || text
    },
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      render: (text) => <Tag color={levelColors[text]}>{text}级</Tag>
    },
    {
      title: '联系人',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 100
    },
    {
      title: '联系电话',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 130
    },
    {
      title: '信用额度',
      dataIndex: 'creditLimit',
      key: 'creditLimit',
      width: 120,
      render: (text) => `¥${parseFloat(text || 0).toLocaleString()}`
    },
    {
      title: '业务员',
      dataIndex: 'salesPerson',
      key: 'salesPerson',
      width: 100,
      render: (salesPerson) => salesPerson?.realName || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (text) => {
        const status = statusMap[text] || { text: text, color: 'default' };
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个客户吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchCustomers();
  }, [pagination.current, pagination.pageSize, searchText, filters]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getList({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        ...filters
      });

      setCustomers(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));
    } catch (error) {
      message.error('获取客户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleEdit = (record) => {
    setEditingCustomer(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await customerAPI.delete(id);
      message.success('删除成功');
      fetchCustomers();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleAdd = () => {
    setEditingCustomer(null);
    form.resetFields();
    // 设置默认值
    form.setFieldsValue({
      type: 'distributor',
      level: 'C',
      status: 'active',
      paymentMethod: 'transfer',
      priceLevel: 'standard',
      creditLimit: 0,
      creditDays: 0
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingCustomer) {
        await customerAPI.update(editingCustomer.id, values);
        message.success('更新成功');
      } else {
        await customerAPI.create(values);
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchCustomers();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '操作失败');
      }
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setSearchText('');
    setFilters({ level: '', type: '', status: '' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>客户管理</h2>
        <Space>
          <ImportExportButtons
            type="customers"
            exportParams={{ search: searchText, ...filters }}
            onImportSuccess={fetchCustomers}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增客户
          </Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Search
              placeholder="搜索编码/名称/联系人/电话"
              onSearch={handleSearch}
              style={{ width: 250 }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="客户等级"
              style={{ width: 120 }}
              allowClear
              value={filters.level || undefined}
              onChange={(value) => handleFilterChange('level', value)}
            >
              <Option value="A">A级</Option>
              <Option value="B">B级</Option>
              <Option value="C">C级</Option>
              <Option value="D">D级</Option>
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="客户类型"
              style={{ width: 120 }}
              allowClear
              value={filters.type || undefined}
              onChange={(value) => handleFilterChange('type', value)}
            >
              <Option value="manufacturer">生产企业</Option>
              <Option value="distributor">经销商</Option>
              <Option value="retailer">零售商</Option>
              <Option value="end_user">终端用户</Option>
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="状态"
              style={{ width: 120 }}
              allowClear
              value={filters.status || undefined}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="active">正常</Option>
              <Option value="inactive">停用</Option>
              <Option value="blacklist">黑名单</Option>
            </Select>
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={customers}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`
        }}
        onChange={handleTableChange}
        scroll={{ x: 1400 }}
      />

      <Modal
        title={editingCustomer ? '编辑客户' : '新增客户'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={800}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="客户编码"
                rules={[{ required: true, message: '请输入客户编码' }]}
              >
                <Input placeholder="请输入客户编码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="客户名称"
                rules={[{ required: true, message: '请输入客户名称' }]}
              >
                <Input placeholder="请输入客户名称" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="shortName" label="客户简称">
                <Input placeholder="请输入客户简称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="type" label="客户类型">
                <Select placeholder="请选择客户类型">
                  <Option value="manufacturer">生产企业</Option>
                  <Option value="distributor">经销商</Option>
                  <Option value="retailer">零售商</Option>
                  <Option value="end_user">终端用户</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="level" label="客户等级">
                <Select placeholder="请选择客户等级">
                  <Option value="A">A级（重要客户）</Option>
                  <Option value="B">B级（普通客户）</Option>
                  <Option value="C">C级（一般客户）</Option>
                  <Option value="D">D级（潜在客户）</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="industry" label="所属行业">
                <Input placeholder="如：机械加工、汽车制造等" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="region" label="所属区域">
                <Input placeholder="如：华东、华南等" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="contactPerson" label="联系人">
                <Input placeholder="请输入联系人" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contactPhone" label="联系电话">
                <Input placeholder="请输入联系电话" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contactEmail" label="联系邮箱">
                <Input placeholder="请输入联系邮箱" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="address" label="地址">
            <Input placeholder="请输入详细地址" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="creditLimit" label="信用额度">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  prefix="¥"
                  placeholder="请输入信用额度"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="creditDays" label="信用天数">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={0}
                  addonAfter="天"
                  placeholder="请输入信用天数"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="paymentMethod" label="付款方式">
                <Select placeholder="请选择付款方式">
                  <Option value="cash">现金</Option>
                  <Option value="transfer">银行转账</Option>
                  <Option value="check">支票</Option>
                  <Option value="acceptance">承兑汇票</Option>
                  <Option value="credit">赊销</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="taxNumber" label="税号">
                <Input placeholder="请输入税号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priceLevel" label="价格等级">
                <Select placeholder="请选择价格等级">
                  <Option value="vip">VIP价</Option>
                  <Option value="wholesale">批发价</Option>
                  <Option value="standard">标准价</Option>
                  <Option value="retail">零售价</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bankName" label="开户银行">
                <Input placeholder="请输入开户银行" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="bankAccount" label="银行账号">
                <Input placeholder="请输入银行账号" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="状态">
                <Select placeholder="请选择状态">
                  <Option value="active">正常</Option>
                  <Option value="inactive">停用</Option>
                  <Option value="blacklist">黑名单</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={3} placeholder="请输入备注信息" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerList;
